import { useState, useEffect } from 'react';

// Tailwind CSS breakpoints
const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

/**
 * Custom hook for responsive behavior using ResizeObserver
 * Provides dynamic screen size detection that updates on window resize
 */
export const useResponsive = () => {
  const [screenSize, setScreenSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    isSmall: typeof window !== 'undefined' ? window.innerWidth < BREAKPOINTS.md : false,
    isMedium: typeof window !== 'undefined' ?
      window.innerWidth >= BREAKPOINTS.md && window.innerWidth < BREAKPOINTS.lg : false,
    isLarge: typeof window !== 'undefined' ? window.innerWidth >= BREAKPOINTS.lg : false,
  });

  useEffect(() => {
    // Function to update screen size state
    const updateScreenSize = () => {
      const width = window.innerWidth;
      setScreenSize({
        width,
        isSmall: width < BREAKPOINTS.md,
        isMedium: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
        isLarge: width >= BREAKPOINTS.lg,
      });
    };

    // Use ResizeObserver if available, fallback to resize event
    let resizeObserver;

    if (typeof ResizeObserver !== 'undefined') {
      // ResizeObserver for better performance
      resizeObserver = new ResizeObserver(updateScreenSize);
      resizeObserver.observe(document.documentElement);
    } else {
      // Fallback to resize event
      window.addEventListener('resize', updateScreenSize);
    }

    // Initial size calculation
    updateScreenSize();

    // Cleanup
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', updateScreenSize);
      }
    };
  }, []);

  return screenSize;
};

/**
 * Hook specifically for checking if screen is small (< 768px)
 * Common use case for mobile-first responsive design
 */
export const useIsSmallScreen = () => {
  const { isSmall } = useResponsive();
  return isSmall;
};

